
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';


/**
 * Comprueba si los bytes proporcionados corresponden a un PDF válido
 * @param {ArrayBuffer} bytes - Bytes a verificar
 * @returns {boolean} - True si parece ser un PDF válido
 */
export const esPDFValido = (bytes) => {
  if (!bytes || !(bytes instanceof ArrayBuffer)) {
    console.error('esPDFValido: Los bytes proporcionados no son un ArrayBuffer válido', bytes);
    return false;
  }

  if (bytes.byteLength < 5) {
    console.error('esPDFValido: El ArrayBuffer es demasiado pequeño para ser un PDF válido:', bytes.byteLength, 'bytes');
    return false;
  }

  // Un PDF válido comienza con %PDF-
  const header = new Uint8Array(bytes, 0, 5);
  const headerString = String.fromCharCode.apply(null, header);

  // Convertir los bytes a valores hexadecimales para mejor diagnóstico
  const hexValues = Array.from(header).map(byte => byte.toString(16).padStart(2, '0'));
  console.log('esPDFValido: Bytes hexadecimales:', hexValues.join(' '));

  const isValid = headerString === '%PDF-';
  if (!isValid) {
    console.error('esPDFValido: El archivo no comienza con %PDF-');
  }

  return isValid;
};

/**
 * Cargar la plantilla PDF para las recetas médicas
 * @param {string} rutaPersonalizada - Ruta a la plantilla personalizada (opcional)
 * @returns {Promise<ArrayBuffer>} Bytes de la plantilla PDF
 */
export const cargarPlantillaPDF = async (rutaPersonalizada = null) => {
  try {
    // Ruta predeterminada (la sabemos correcta)
    let rutaFinal = '/data/formato_receta.pdf'; // Esta ruta apunta a public/data/formato_receta.pdf

    // Si se proporcionó una ruta personalizada, usarla en su lugar
    if (rutaPersonalizada) {
      console.log('Usando ruta personalizada:', rutaPersonalizada);
      rutaFinal = rutaPersonalizada;
    } else {
      console.log('Usando ruta predeterminada:', rutaFinal);
    }

    // Realizar el fetch directamente a la ruta principal
    console.log('Cargando PDF desde:', rutaFinal);
    const response = await fetch(rutaFinal);

    if (!response.ok) {
      throw new Error(`Error al cargar el PDF. Estado: ${response.status} ${response.statusText}`);
    }

    // Verificar que el content-type sea application/pdf
    const contentType = response.headers.get('content-type');
    console.log('Content-Type del PDF:', contentType);

    // Obtener los bytes del archivo
    const arrayBuffer = await response.arrayBuffer();

    // Verificar que los primeros bytes correspondan a un PDF válido
    const header = new Uint8Array(arrayBuffer, 0, 5);
    const headerString = String.fromCharCode.apply(null, header);
    console.log('Encabezado del archivo:', headerString);

    if (headerString !== '%PDF-') {
      console.warn('El archivo no parece ser un PDF válido. Encabezado:', headerString);
      throw new Error('El contenido descargado no corresponde a un PDF válido.');
    }

    console.log('PDF cargado exitosamente, tamaño:', arrayBuffer.byteLength, 'bytes');
    return arrayBuffer;
  } catch (error) {
    console.error('Error al cargar el PDF:', error.message);
    throw error;
  }
};

/**
 * Genera una receta médica con formato predefinido usando PDF-lib
 * @param {Object|Array} medicamento - Datos del medicamento o array de medicamentos
 * @param {Object} paciente - Datos del paciente
 * @param {Object} diagnostico - Datos del diagnóstico (opcional)
 * @param {string} proximaCita - Fecha de próxima cita (opcional)
 * @param {string} doctor - Nombre del doctor
 * @param {string} rutaPlantilla - Ruta a la plantilla PDF personalizada (opcional)
 * @returns {Promise<boolean>} - True si se generó correctamente
 */
export const generarPDFReceta = async (medicamento, paciente, diagnostico = "", proximaCita = "", doctor = "Dra. Ximena Ríos R.", rutaPlantilla = null) => {
  try {
    console.log('Iniciando generación de receta con pdf-lib');

    // Intentar cargar la plantilla PDF desde la función mejorada
    let plantillaPdfBytes = null;

    try {
      // Siempre intentamos primero con la ruta directa al PDF predeterminado
      console.log('Intentando cargar la plantilla predeterminada directamente');
      try {
        // Forzar el uso de la plantilla en public/data
        plantillaPdfBytes = await fetch('/data/formato_receta.pdf').then(r => r.arrayBuffer());
        console.log('Plantilla predeterminada cargada directamente, tamaño:',
          plantillaPdfBytes.byteLength, 'bytes');
      } catch (directError) {
        console.warn('Error al cargar directamente:', directError.message);

        // Si falla la carga directa, intentamos con la función mejorada
        if (rutaPlantilla) {
          console.log('Intentando cargar plantilla personalizada:', rutaPlantilla);
          plantillaPdfBytes = await cargarPlantillaPDF(rutaPlantilla);
        } else {
          console.log('Intentando cargar plantilla predeterminada con función mejorada');
          plantillaPdfBytes = await cargarPlantillaPDF(null);
        }
      }

      // Validar que los bytes cargados sean realmente un PDF
      if (!esPDFValido(plantillaPdfBytes)) {
        console.error('Los bytes cargados no corresponden a un PDF válido');
        throw new Error('El archivo cargado no es un PDF válido');
      }
    } catch (error) {
      console.error('Error al cargar cualquier plantilla PDF:', error);

      // Si no podemos cargar ninguna plantilla, generamos un PDF desde cero
      console.log('Generando PDF desde cero sin plantilla...');
      return await generarPDFRecetaSinPlantilla(medicamento, paciente, diagnostico, proximaCita, doctor);
    }

    // Cargar el documento PDF con la plantilla como fondo
    const pdfDoc = await PDFDocument.load(plantillaPdfBytes);
    console.log('Plantilla PDF cargada correctamente');

    // Obtener la primera página
    const firstPage = pdfDoc.getPages()[0];

    // Obtener dimensiones de la página
    const { width, height } = firstPage.getSize();
    console.log(`Dimensiones del PDF: ${width}x${height}`);

    // Verificar que las dimensiones coincidan con lo esperado (A4)
    // y ajustar las coordenadas si es necesario
    const esA4Vertical = Math.abs(width - 595.28) < 10 && Math.abs(height - 841.89) < 10;
    const esA4Horizontal = Math.abs(width - 841.89) < 10 && Math.abs(height - 595.28) < 10;

    console.log('PDF es A4 Vertical:', esA4Vertical);
    console.log('PDF es A4 Horizontal:', esA4Horizontal);


    // Configurar fuentes
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Tamaño de letra predeterminado
    const fontSize = 12;

    // Formatear fecha actual
    const fechaActual = new Date().toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // LADO IZQUIERDO (Receta - Rp.)
    // ===========================

    // Fecha en la parte superior izquierda (coordenadas según valores proporcionados)
    // Coordenadas exactas: Esquina superior izquierda: (291.5, 114.79) a Esquina inferior derecha: (314.9, 127.78)
    const fechaX = 321; // Aproximadamente el centro de las coordenadas X
    const fechaY = 473; // Aproximadamente el centro de las coordenadas Y

    firstPage.drawText(fechaActual, {
      x: fechaX,
      y: fechaY,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

function wrapText(text, font, size, maxWidth) {
  // 1) eliminamos saltos de línea
  const sanitized = text.replace(/\r?\n+/g, ' ');
  
  // 2) el resto igual que antes
  const words = sanitized.split(' ');
  const lines = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (font.widthOfTextAtSize(test, size) > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}


    // Recetas/medicamentos (Rp.)
    // Posición para los medicamentos en el lado izquierdo
    const medicamentosX = 40; // Posición X para medicamentos
    const medicamentosY = 400; // Posición Y inicial para el primer medicamento

    const leftX = medicamentosX;     // ej. 90
    const leftWidth = 390;               // ancho interior de “Rp.”
    const rightX = 436;     // ej. 500
    const rightWidth = 390;               // ancho interior de “Indicaciones”
    const lineHeight = fontSize + 2;      // espacio vertical entre líneas



    // Si es un solo medicamento o un array
    const medicamentos = Array.isArray(medicamento) ? medicamento : [medicamento];
    let yPosLeft = medicamentosY;   // ej. 400
    let yPosRight = medicamentosY;   // mismo Y para sincronizar alturas

    medicamentos.forEach(med => {
      const nombre = med.nombre || 'Sin nombre';
      const dosis = med.dosis || '';
      const freq = med.frecuencia || '';
      const dur = med.duracion || '';
      const indic = med.indicaciones || '';

      // ——— Caja izquierda ———
      // Línea 1: nombre
      const leftLines = wrapText(nombre, helveticaBoldFont, fontSize, leftWidth);
      leftLines.forEach(line => {
        firstPage.drawText(line, { x: leftX, y: yPosLeft, size: fontSize, font: helveticaBoldFont });
        yPosLeft -= lineHeight;
      });
      // Línea 2: dosis con sangría
      const dosisLine = '    ' + dosis; // cuatro espacios
      const dosisWrapped = wrapText(dosisLine, helveticaFont, fontSize, leftWidth);
      dosisWrapped.forEach(line => {
        firstPage.drawText(line, { x: leftX, y: yPosLeft, size: fontSize, font: helveticaFont });
        yPosLeft -= lineHeight;
      });

      // Dejar un huequito antes del siguiente medicamento
      yPosLeft -= lineHeight;

      // ——— Caja derecha ———
      // Línea 1: nombre
      let y = yPosRight;
      const rightNameLines = wrapText(nombre, helveticaBoldFont, fontSize, rightWidth);
      rightNameLines.forEach(line => {
        firstPage.drawText(line, { x: rightX, y, size: fontSize, font: helveticaBoldFont });
        y -= lineHeight;
      });
      // Línea 2: frecuencia – duración con sangría
      const freqDurLine = '    ' + [freq, dur].filter(Boolean).join(' - ');
      const freqDurWrapped = wrapText(freqDurLine, helveticaFont, fontSize, rightWidth);
      freqDurWrapped.forEach(line => {
        firstPage.drawText(line, { x: rightX, y, size: fontSize, font: helveticaFont });
        y -= lineHeight;
      });
      // Línea 3: indicaciones
      const indicWrapped = wrapText(indic, helveticaFont, fontSize, rightWidth);
      indicWrapped.forEach(line => {
        firstPage.drawText(line, { x: rightX, y, size: fontSize, font: helveticaFont });
        y -= lineHeight;
      });

      // Avanzar el cursor derecho al máximo de lo dibujado
      yPosRight = Math.min(yPosRight - ((leftLines.length + dosisWrapped.length + 1) * lineHeight), y);

      // Prevenir que ambas columnas se solapen
      const nextY = Math.min(yPosLeft, yPosRight);
      yPosLeft = yPosRight = nextY - lineHeight;
    });


    // Diagnóstico (Dg.)
    const diagnosticoX = 52;
    const diagnosticoY = 88; // Parte inferior izquierda del documento

    if (diagnostico && (typeof diagnostico === 'string' ? diagnostico : diagnostico.descripcion)) {
      const diagTexto = typeof diagnostico === 'string' ? diagnostico : diagnostico.descripcion;

      firstPage.drawText(diagTexto, {
        x: diagnosticoX,
        y: diagnosticoY,
        size: fontSize,
        font: helveticaFont,
        color: rgb(0, 0, 0)
      });
      console.log(`Diagnóstico dibujado en (${diagnosticoX}, ${diagnosticoY})`);
    }

    // LADO DERECHO (Indicaciones)
    // ===========================

    // Nombre del paciente - Parte superior derecha (cerca de la segunda fecha)
    // Estimamos la posición basada en la segunda fecha, pero más a la izquierda
    const pacienteX = 491; // Un poco a la izquierda de la segunda fecha
    const pacienteY = 448; // Misma altura que la segunda fecha

    if (paciente) {
      const nombreCompleto = `${paciente.nombre || ''} ${paciente.apellido || ''}`.trim();
      if (nombreCompleto) {
        firstPage.drawText(nombreCompleto, {
          x: pacienteX,
          y: pacienteY,
          size: fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0)
        });
        console.log(`Nombre del paciente dibujado en (${pacienteX}, ${pacienteY})`);
      }
    }

    // Fecha (lado derecho)
    // Coordenadas exactas: Esquina superior izquierda: (703.75, 110.79) a Esquina inferior derecha: (727.15, 123.77)
    const fechaDerechaX = 733;
    const fechaDerechaY = 476;

    firstPage.drawText(fechaActual, {
      x: fechaDerechaX,
      y: fechaDerechaY,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

    // Indicaciones generales (lado derecho)
    const indicacionesX = 500; // Lado derecho
    const indicacionesY = height - 200; // Misma altura que los medicamentos

    let textoIndicaciones = '';

    // Buscar indicaciones generales
    if (medicamentos[0] && medicamentos[0].indicacionesGenerales) {
      textoIndicaciones = medicamentos[0].indicacionesGenerales;
    } else if (typeof medicamento === 'object' && medicamento.indicaciones) {
      textoIndicaciones = medicamento.indicaciones;
    }

    // Añadir indicaciones específicas si existen
    if (medicamentos[0] && medicamentos[0].indicacionesEspecificas) {
      if (textoIndicaciones) {
        textoIndicaciones += '\n\nIndicaciones Específicas:\n' + medicamentos[0].indicacionesEspecificas;
      } else {
        textoIndicaciones = 'Indicaciones Específicas:\n' + medicamentos[0].indicacionesEspecificas;
      }
    }

    if (textoIndicaciones) {
      // Posición inicial para las indicaciones
      let indicY = indicacionesY;
      const maxWidth = 250; // Ancho máximo en puntos
      const lineHeight = 18; // Altura de línea

      // Dividir indicaciones en múltiples líneas
      let palabras = textoIndicaciones.split(' ');
      let lineaActual = '';

      for (let i = 0; i < palabras.length; i++) {
        const testLine = lineaActual + palabras[i] + ' ';
        const testWidth = helveticaFont.widthOfTextAtSize(testLine, fontSize - 1);

        if (testWidth > maxWidth) {
          firstPage.drawText(lineaActual, {
            x: indicacionesX,
            y: indicY,
            size: fontSize - 1,
            font: helveticaFont,
            color: rgb(0, 0, 0)
          });

          lineaActual = palabras[i] + ' ';
          indicY -= lineHeight;
        } else {
          lineaActual = testLine;
        }
      }

      // Dibujar la última línea si queda algo
      if (lineaActual.trim()) {
        firstPage.drawText(lineaActual, {
          x: indicacionesX,
          y: indicY,
          size: fontSize - 1,
          font: helveticaFont,
          color: rgb(0, 0, 0)
        });
      }

      console.log(`Indicaciones dibujadas, iniciando en (${indicacionesX}, ${indicacionesY})`);
    }

    // Próxima cita (lado derecho)
    const citaX = 500;
    const citaY = 200; // Misma altura que el diagnóstico, pero en lado derecho

    if (proximaCita) {
      firstPage.drawText(proximaCita, {
        x: citaX,
        y: citaY,
        size: fontSize,
        font: helveticaFont,
        color: rgb(0, 0, 0)
      });
      console.log(`Próxima cita dibujada en (${citaX}, ${citaY})`);
    }

    // Generar PDF final
    const pdfBytes = await pdfDoc.save();

    // Crear blob y descargar
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `Receta_${paciente ? paciente.cedula || paciente.nombre : 'Paciente'}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('Receta generada y descargada correctamente');
    return true;
  } catch (error) {
    console.error('Error al generar PDF de receta:', error);
    // En caso de error grave, intentar generar una receta sin plantilla
    try {
      console.log('Intentando generar receta sin plantilla como fallback...');
      return await generarPDFRecetaSinPlantilla(medicamento, paciente, diagnostico, proximaCita, doctor);
    } catch (fallbackError) {
      console.error('Error en fallback de generación de receta:', fallbackError);
      alert(`Error al generar la receta: ${error.message}. El fallback también falló.`);
      return false;
    }
  }
};

/**
 * Genera una receta médica simple desde cero sin usar plantilla
 * @param {Object|Array} medicamento - Datos del medicamento o array de medicamentos
 * @param {Object} paciente - Datos del paciente
 * @param {Object|string} diagnostico - Datos del diagnóstico (opcional)
 * @param {string} proximaCita - Fecha de próxima cita (opcional)
 * @param {string} doctor - Nombre del doctor
 * @returns {Promise<boolean>} - True si se generó correctamente
 */
export const generarPDFRecetaSinPlantilla = async (medicamento, paciente, diagnostico = "", proximaCita = "", doctor = "Dra. Ximena Ríos R.") => {
  try {
    console.log('Generando PDF de receta desde cero (sin plantilla)');

    // Crear nuevo documento PDF
    const pdfDoc = await PDFDocument.create();

    // Añadir una página en blanco (tamaño A4)
    const page = pdfDoc.addPage([595, 842]);

    // Obtener dimensiones de la página
    const { width, height } = page.getSize();

    // Configurar fuentes
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const timesBoldFont = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);

    // Tamaño de letra predeterminado
    const fontSize = 12;

    // Formatear fecha actual
    const fechaActual = new Date().toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // Encabezado con título centrado
    page.drawText('RECETA MÉDICA', {
      x: width / 2 - 60,
      y: height - 50,
      size: 18,
      font: timesBoldFont,
      color: rgb(0, 0, 0)
    });

    // Información del doctor
    page.drawText(doctor, {
      x: width / 2 - (doctor.length * 5) / 2,
      y: height - 75,
      size: 14,
      font: helveticaBoldFont,
      color: rgb(0, 0, 0)
    });

    // Línea divisoria
    page.drawLine({
      start: { x: 50, y: height - 90 },
      end: { x: width - 50, y: height - 90 },
      thickness: 1,
      color: rgb(0.7, 0.7, 0.7),
    });

    // INFORMACIÓN DEL PACIENTE
    // Fecha
    page.drawText(`Fecha: ${fechaActual}`, {
      x: width - 150,
      y: height - 120,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

    // Datos del paciente
    if (paciente) {
      const nombreCompleto = `${paciente.nombre || ''} ${paciente.apellido || ''}`.trim();

      page.drawText('Paciente:', {
        x: 50,
        y: height - 120,
        size: fontSize,
        font: helveticaBoldFont,
        color: rgb(0, 0, 0)
      });

      page.drawText(nombreCompleto, {
        x: 120,
        y: height - 120,
        size: fontSize,
        font: helveticaFont,
        color: rgb(0, 0, 0)
      });

      if (paciente.cedula) {
        page.drawText(`Cédula: ${paciente.cedula}`, {
          x: 50,
          y: height - 140,
          size: fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0)
        });
      }
    }

    // Línea divisoria
    page.drawLine({
      start: { x: 50, y: height - 160 },
      end: { x: width - 50, y: height - 160 },
      thickness: 1,
      color: rgb(0.7, 0.7, 0.7),
    });

    // MEDICAMENTOS
    page.drawText('PRESCRIPCIÓN:', {
      x: 50,
      y: height - 185,
      size: 14,
      font: helveticaBoldFont,
      color: rgb(0, 0, 0)
    });

    let yPosition = height - 215;

    // Procesar medicamentos
    const medicamentos = Array.isArray(medicamento) ? medicamento : [medicamento];

    medicamentos.forEach((med, index) => {
      // Número de medicamento
      page.drawText(`${index + 1}.`, {
        x: 50,
        y: yPosition,
        size: fontSize,
        font: helveticaBoldFont,
        color: rgb(0, 0, 0)
      });

      // Nombre del medicamento
      const nombreMedicamento = med.nombre || 'Sin nombre';
      page.drawText(nombreMedicamento, {
        x: 70,
        y: yPosition,
        size: fontSize,
        font: helveticaBoldFont,
        color: rgb(0, 0, 0)
      });

      yPosition -= 20;

      // Dosis y frecuencia
      if (med.dosis) {
        page.drawText(`Dosis: ${med.dosis}`, {
          x: 70,
          y: yPosition,
          size: fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0)
        });

        yPosition -= 20;
      }

      if (med.frecuencia) {
        page.drawText(`Frecuencia: ${med.frecuencia}`, {
          x: 70,
          y: yPosition,
          size: fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0)
        });

        yPosition -= 20;
      }

      // Indicaciones específicas
      if (med.indicaciones) {
        page.drawText(`Indicaciones: ${med.indicaciones}`, {
          x: 70,
          y: yPosition,
          size: fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0)
        });

        yPosition -= 20;
      }

      // Espacio entre medicamentos
      yPosition -= 10;
    });

    // INDICACIONES GENERALES
    let textoIndicaciones = '';

    if (medicamentos[0] && medicamentos[0].indicacionesGenerales) {
      textoIndicaciones = medicamentos[0].indicacionesGenerales;
    } else if (typeof medicamento === 'object' && medicamento.indicaciones) {
      textoIndicaciones = medicamento.indicaciones;
    }

    if (textoIndicaciones) {
      yPosition -= 10;

      page.drawText('INDICACIONES GENERALES:', {
        x: 50,
        y: yPosition,
        size: 14,
        font: helveticaBoldFont,
        color: rgb(0, 0, 0)
      });

      yPosition -= 25;

      // Escribir indicaciones generales con ajuste de línea
      const maxWidth = width - 100;
      const lineHeight = 20;

      let palabras = textoIndicaciones.split(' ');
      let lineaActual = '';

      for (let i = 0; i < palabras.length; i++) {
        const testLine = lineaActual + palabras[i] + ' ';
        const testWidth = helveticaFont.widthOfTextAtSize(testLine, fontSize);

        if (testWidth > maxWidth) {
          page.drawText(lineaActual, {
            x: 50,
            y: yPosition,
            size: fontSize,
            font: helveticaFont,
            color: rgb(0, 0, 0)
          });

          lineaActual = palabras[i] + ' ';
          yPosition -= lineHeight;
        } else {
          lineaActual = testLine;
        }
      }

      // Dibujar la última línea
      if (lineaActual.trim()) {
        page.drawText(lineaActual, {
          x: 50,
          y: yPosition,
          size: fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0)
        });

        yPosition -= lineHeight;
      }
    }

    // DIAGNÓSTICO
    if (diagnostico && (typeof diagnostico === 'string' ? diagnostico : diagnostico.descripcion)) {
      const diagTexto = typeof diagnostico === 'string' ? diagnostico : diagnostico.descripcion;

      yPosition -= 20;

      page.drawText('DIAGNÓSTICO:', {
        x: 50,
        y: yPosition,
        size: 14,
        font: helveticaBoldFont,
        color: rgb(0, 0, 0)
      });

      yPosition -= 25;

      page.drawText(diagTexto, {
        x: 50,
        y: yPosition,
        size: fontSize,
        font: helveticaFont,
        color: rgb(0, 0, 0)
      });

      yPosition -= 20;
    }

    // PRÓXIMA CITA
    if (proximaCita) {
      yPosition -= 20;


      yPosition -= 25;

      page.drawText(proximaCita, {
        x: 50,
        y: yPosition,
        size: fontSize,
        font: helveticaFont,
        color: rgb(0, 0, 0)
      });
    }

    // Firma del doctor
    page.drawText('Firma del médico:', {
      x: width - 200,
      y: 100,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

    // Línea para firma
    page.drawLine({
      start: { x: width - 200, y: 80 },
      end: { x: width - 50, y: 80 },
      thickness: 1,
      color: rgb(0, 0, 0),
    });

    // Nombre del doctor debajo de la firma
    page.drawText(doctor, {
      x: width - 200 + (150 - doctor.length * 4) / 2, // Centrar bajo la línea
      y: 60,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

    // Generar PDF final
    const pdfBytes = await pdfDoc.save();

    // Crear blob y descargar
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `Receta_${paciente ? paciente.cedula || paciente.nombre : 'Paciente'}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('Receta generada sin plantilla y descargada correctamente');
    return true;
  } catch (error) {
    console.error('Error al generar PDF de receta sin plantilla:', error);
    alert(`Error al generar la receta: ${error.message}`);
    return false;
  }
};

/**
 * Obtiene la configuración de receta del doctor desde el estado o la base de datos
 * @returns {Promise<Object>} - Objeto con la configuración de la receta
 */
export const obtenerConfiguracionReceta = async () => {
  try {
    // Importar el servicio de forma dinámica para evitar problemas de dependencia circular
    const { obtenerConfiguracionReceta } = await import('../api/doctorService');
    const response = await obtenerConfiguracionReceta();

    if (response && response.data) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error al obtener configuración de receta:', error);
    return null;
  }
};

/**
 * Función para generar PDF con todas las recetas usando pdf-lib
 * @param {Array} medicamentos - Array de medicamentos
 * @param {Object} paciente - Datos del paciente
 * @param {Object|string} diagnostico - Datos del diagnóstico (opcional)
 * @param {string} proximaCita - Fecha de próxima cita (opcional)
 * @param {string} doctor - Nombre del doctor
 * @param {string} rutaPlantilla - Ruta a la plantilla PDF personalizada (opcional)
 * @returns {Promise<boolean>} - True si se generó correctamente
 */
export const generarPDFTodasRecetas = async (medicamentos, paciente, diagnostico = "", proximaCita = "", doctor = "Dra. Ximena Ríos R.", rutaPlantilla = null) => {
  if (!medicamentos || medicamentos.length === 0) return false;

  try {
    // Intentar obtener la configuración de la receta si no se proporcionó una ruta de plantilla
    if (!rutaPlantilla) {
      const config = await obtenerConfiguracionReceta();
      if (config && config.rutaPlantillaPDF) {
        rutaPlantilla = config.rutaPlantillaPDF;
        console.log('Usando plantilla de configuración:', rutaPlantilla);
      }
    }

    // Si hay varias recetas, generar un PDF para cada una
    for (let i = 0; i < medicamentos.length; i++) {
      await generarPDFReceta(medicamentos[i], paciente, diagnostico, proximaCita, doctor, rutaPlantilla);
    }
    return true;
  } catch (error) {
    console.error('Error al generar PDF de todas las recetas:', error);
    return false;
  }
};